import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { Tooltip } from "@material-ui/core";
import html2canvas from "html2canvas";
import { ModalComponent } from "components/ui/modal/Modal";
import ReportList from "components/data-display/report-list/ReportList";

import "./ScreenshotPotree.css";

const HINT_DURATION = 5000;

export function ScreenshotPotree({ tagReference, viewerRef, folderPath, compareButton }) {
  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [image, setImage] = useState({ img: null, base64: "" });

  // Capture hint
  const reports = useSelector(state => state.report?.report || []);
  const prevCountRef = useRef(reports.length);
  const [showHint, setShowHint] = useState(false);
  const [hintLeaving, setHintLeaving] = useState(false);
  const [hintStyle, setHintStyle] = useState({});
  const toolbarRef = useRef(null);
  const dismissTimerRef = useRef(null);

  const dismissHint = () => {
    setHintLeaving(true);
    setTimeout(() => { setShowHint(false); setHintLeaving(false); }, 260);
  };

  const computeHintPos = () => {
    if (!toolbarRef.current) return;
    const rect = toolbarRef.current.getBoundingClientRect();
    setHintStyle({
      position: "fixed",
      bottom: window.innerHeight - rect.top + 10,
      left: Math.max(8, rect.left + rect.width / 2 - 128),
      top: "auto",
      right: "auto",
    });
  };

  useEffect(() => {
    if (reports.length > prevCountRef.current) {
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      computeHintPos();
      setShowHint(true);
      setHintLeaving(false);
      dismissTimerRef.current = setTimeout(dismissHint, HINT_DURATION);
    }
    prevCountRef.current = reports.length;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports.length]);

  useEffect(() => {
    return () => { if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current); };
  }, []);

  function handleOpen() { setIsOpenModal(true); }
  function handleClose() { setIsOpenModal(false); }

  const capturePotree = () =>
    new Promise((resolve, reject) => {
      const viewer = viewerRef?.current;
      if (!viewer?.renderer?.domElement) {
        reject(new Error("Viewer não disponível"));
        return;
      }

      // Force a render then capture inside the same animation frame
      // so the drawing buffer is guaranteed to be filled.
      requestAnimationFrame(() => {
        try {
          // If edlRenderer exists (EDL enabled) use makeScreenshot for
          // a proper off-screen capture, otherwise hit domElement directly.
          if (viewer.edlRenderer) {
            const { width, height, buffer } = viewer.edlRenderer.makeScreenshot();
            const offCanvas = document.createElement("canvas");
            offCanvas.width = width;
            offCanvas.height = height;
            const ctx = offCanvas.getContext("2d");
            ctx.putImageData(new ImageData(new Uint8ClampedArray(buffer), width, height), 0, 0);
            resolve(offCanvas);
          } else {
            // preserveDrawingBuffer: true is set by Potree — safe to read here
            resolve(viewer.renderer.domElement);
          }
        } catch (err) {
          reject(err);
        }
      });
    });

  const openModal = async e => {
    e.preventDefault();
    setIsLoadingModal(true);
    try {
      let screenshot;

      if (viewerRef?.current) {
        screenshot = await capturePotree();
      } else if (tagReference?.current) {
        if (tagReference.current.components?.screenshot) {
          screenshot = tagReference.current.components.screenshot.getCanvas("perspective");
        } else {
          screenshot = await html2canvas(tagReference.current, { useCORS: true, logging: false });
        }
      }

      if (!screenshot) return;

      const image64 = screenshot.toDataURL("image/png");
      const imageJpg = await new Promise(resolve => {
        screenshot.toBlob(
          blob => {
            if (!blob) { resolve(null); return; }
            resolve(new File([blob], "screenshot.jpg", { type: "image/jpeg" }));
          },
          "image/jpeg",
          1
        );
      });

      if (imageJpg) {
        setImage({ img: imageJpg, base64: image64 });
        handleOpen();
      }
    } catch (err) {
      console.error("Erro ao capturar screenshot Potree:", err);
    } finally {
      setIsLoadingModal(false);
    }
  };

  const toolbarClass = [
    "screenshot-potree",
    "screenshot-toolbar",
    showHint ? "screenshot-toolbar--hint" : ""
  ].filter(Boolean).join(" ");

  return (
    <>
      <div ref={toolbarRef} className={toolbarClass}>
        {compareButton}
        {isLoadingModal ? (
          <div className="screenshot-potree__spinner" role="status">
            <div className="spinner-border text-primary" />
          </div>
        ) : (
          <Tooltip title="Capturar e criar ocorrência (lápis, círculo, quadrado e cores)">
            <button
              type="button"
              onClick={openModal}
              className="screenshot-toolbar__btn screenshot-potree__btn"
              aria-label="Capturar snapshot e anotar"
            >
              <i className="fas fa-camera" />
            </button>
          </Tooltip>
        )}
        <ReportList folderPath={folderPath} />
      </div>

      {showHint && createPortal(
        <div
          className={`capture-hint capture-hint--arrow-bottom${hintLeaving ? " capture-hint--leaving" : ""}`}
          style={{ ...hintStyle, "--hint-duration": `${HINT_DURATION}ms` }}
          onClick={dismissHint}
          role="status"
          aria-live="polite"
        >
          <div className="capture-hint__body">
            <div className="capture-hint__check">
              <i className="fas fa-check" />
            </div>
            <div className="capture-hint__text">
              <span className="capture-hint__title">Captura registrada</span>
              <span className="capture-hint__sub">
                Clique em <i className="fas fa-file-alt" /> para revisar e salvar
              </span>
            </div>
            <div className="capture-hint__arrow">
              <i className="fas fa-chevron-right" />
            </div>
          </div>
          <div className="capture-hint__timer">
            <div className="capture-hint__timer-fill" />
          </div>
        </div>,
        document.body
      )}

      {createPortal(
        <ModalComponent
          show={isOpenModal}
          closeModal={handleClose}
          image={image}
        />,
        document.body
      )}
    </>
  );
}
